/** @jsxImportSource @emotion/react */

import {
  layerDataHazardIdTransformer,
  useLayerDataFetcher,
} from '../../../data_fetchers/layerDataFetcher'
import { HazardControls } from './HazardControls'

import { FEATURE_FLAGS } from '@app/app-constants'
import { HazardDetail } from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { ActiveWorkspaceHeader } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/ActiveWorkspaceHeader/ActiveWorkspaceHeader'
import { LayersOrganizer } from '@app/components/Molecules/RiskSideDrawerContent/tabs/InformativePanel/LayersTab/LayersOrganizer'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { usePreferences } from '@app/hooks/usePreferences'
import { useLiveContext } from '@app/modules/Live/contexts/Live.Context'
import { useRiskLayersState } from '@app/risk-migration/hooks/useRiskLayersState'
import type { MapLayer } from '@app/types/layerTypes'
import { Box, Collapse, Typography, useTheme } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { SideDrawerDivider } from '../../../components/SideDrawerDivider'
import { SideDrawerFooter } from '../../../components/SideDrawerFooter'
import { EmptySideDrawer } from '../../EmptyTab'
import { HazardGroup } from '../InformativePanel'
import { LayersTab_Legacy } from './HazardControls/_Legacy/LayersTab/LayersTab'
import {
  hazardControlToggle,
  layerOrganizerContainer,
  layersTabParent,
  mapLayerHeader,
} from './LayersTab.styles'
export const LayersTab = () => {
  const theme = useTheme()
  const { useFeatureFlag } = useFeatureFlags()
  const isNewHazardControlsEnabled = useFeatureFlag(FEATURE_FLAGS.NEW_HAZARD_CONTROLS)
  const { layers } = useRiskLayersState()
  const layersData = useLayerDataFetcher()
  const transformedLayerData = layerDataHazardIdTransformer(layersData)
  const { term_preference } = usePreferences()
  const [openHazardControls, setOpenHazardControls] = useState(true)
  const { liveMapSelected } = useLiveContext()

  const clientName = localStorage.getItem('client_name')

  const hazardLayers = layers.filter((layer) => layer.layerType === 'hazard')

  /**
   * Get the dialog layer data that matches the hazardMapLayers
   */
  const filteredLayersData = useMemo(() => {
    const hazardMapLayers = layers.filter((layer: MapLayer) => layer.layerType === 'hazard')
    const res = layersData?.hazardDialogData.hazardDetails.filter((hazardDetail: HazardDetail) => {
      // filter based on title
      return hazardMapLayers.some((layer: MapLayer) => {
        if (layer.hazard_id) {
          return hazardDetail.scenarios.some((scenario) => {
            return layer.hazard_id === scenario.hazard_id
          })
        }
        return layer.hazard_title === hazardDetail.title
      })
    })
    return res
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersData, layers, clientName])

  function groupHazardsByType(hazardLayers: MapLayer[]): HazardGroup[] {
    const hazardLayersByType: HazardGroup[] = []
    hazardLayers.forEach((readonly_layer) => {
      const layer = { ...readonly_layer }
      const hazardType = layer.hazard_title ?? layer.display_name.split('(')[0].trim()
      let hazardScenario = layer.display_name
      if (layer.scenario_name) {
        hazardScenario = layer.scenario_name
      } else if (hazardScenario.includes('(')) {
        hazardScenario = hazardScenario.split('(')[1].split(')')[0].trim()
        layer.scenario_name = hazardScenario
      }
      const hazardGroup = hazardLayersByType.find((group) => group.hazard_type === hazardType)
      if (hazardGroup) {
        hazardGroup.scenarios.push({ scenario: hazardScenario, layer })
      } else {
        hazardLayersByType.push({
          hazard_type: hazardType,
          scenarios: [{ scenario: hazardScenario, layer }],
        })
      }
    })
    return hazardLayersByType
  }

  const hasHazardControls = filteredLayersData?.some((hazardDetail) => hazardDetail.form.length > 0)
  const hasAtLeastOneUsableControl = useCallback(
    (hazardLayers: MapLayer[]) => {
      // if there is no transformed data (no hazard_id), do the same operations but with filteredLayersData instead
      if (!transformedLayerData) {
        return filteredLayersData?.some((hazardDetail) => {
          const paramObj: { [key: string]: (string | number)[] } = {}
          for (const scenario of hazardDetail.scenarios) {
            for (const paramKey of Object.keys(scenario.parameters)) {
              if (paramObj[paramKey]) {
                paramObj[paramKey].push(scenario.parameters[paramKey])
              } else {
                paramObj[paramKey] = [scenario.parameters[paramKey]]
              }
            }
          }
          return Object.keys(paramObj).some((paramKey) => {
            return paramObj[paramKey].length > 1
          })
        })
      } else {
        const hazardLayersGrouped = groupHazardsByType(hazardLayers)
        const filteredTransformedLayerData = Object.keys(transformedLayerData).filter((key) => {
          const foundHazardLayer = hazardLayersGrouped.find((hazardLayer) => {
            return hazardLayer.scenarios.some((scenario) => {
              return `${scenario.layer.hazard_id}` === `${key}`
            })
          })
          return !!foundHazardLayer
        })
        return filteredTransformedLayerData.some((hazardId) => {
          if (
            !hazardLayersGrouped.some((layer) =>
              layer.scenarios.some((scenario) => {
                return scenario.layer.hazard_id == hazardId
              }),
            )
          ) {
            return false
          }
          return Object.keys(transformedLayerData[hazardId].availableParameterValues).some(
            (parameterKey) => {
              return (
                transformedLayerData[hazardId].availableParameterValues[parameterKey].length > 1
              )
            },
          )
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hazardLayers, clientName, transformedLayerData, filteredLayersData],
  )

  if (!isNewHazardControlsEnabled) {
    return <LayersTab_Legacy />
  }
  return layers.length > 0 ? (
    <>
      <ActiveWorkspaceHeader />
      <Box css={layersTabParent(theme)} className="tutorial-layer-organizer">
        <Box
          css={{
            height: '100%',
            overflowY: 'auto',
            padding: '16px',
          }}
        >
          {hazardLayers.length > 0 &&
            hasAtLeastOneUsableControl(hazardLayers) &&
            hasHazardControls && (
              <>
                <Box css={mapLayerHeader(theme)}>
                  <span>{term_preference.hazard ? 'Hazards' : 'Risk Sources'}</span>
                  <Box
                    css={hazardControlToggle(theme)}
                    onClick={() => setOpenHazardControls((prev) => !prev)}
                  >
                    {openHazardControls ? 'Hide Controls' : 'Show Controls'}
                  </Box>
                </Box>
                <Typography css={{ padding: '0 8px', marginBottom: '16px' }}>
                  Adjust the button controls below to view different scenarios. Set the dominant
                  filter by re-ordering attributes using the drag button.
                </Typography>
                <Collapse in={openHazardControls} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      overflowY: 'auto',
                      flexShrink: 1,
                    }}
                  >
                    <HazardControls
                      layersData={layersData}
                      scenarioByHazardId={transformedLayerData}
                      hazardIds={layers
                        .filter((l) => l.layerType == 'hazard')
                        .map((l) => l.hazard_id ?? l.display_name)
                        .toSorted()}
                    />
                  </Box>
                </Collapse>
              </>
            )}
        </Box>
        <Box className="bottom-shadow" />
        <SideDrawerDivider />
        <Box css={layerOrganizerContainer}>
          <LayersOrganizer />
        </Box>
      </Box>
      <SideDrawerFooter />
    </>
  ) : (
    <EmptySideDrawer
      text={
        liveMapSelected
          ? 'Add layers to start building your operating picture.'
          : 'No layers are visible on the map. Add layers to gain insights'
      }
      type="element"
      buttonText="Add layers"
    />
  )
}
